import { onMounted, ref } from 'vue'

import { useRegisterAdSlot } from './use-ad-slots'
import { queueAdEvent } from '../utils/queue-ad-event'

import type { AdSlotType } from '../types/ad-slot-types'

type AdStatus = 'loading' | 'successful' | 'failed'

export function useAd (type: AdSlotType, index: number) {
  const { register } = useRegisterAdSlot()
  const slotName = ref<string | null>(null)
  const status = ref<AdStatus>('loading')
  const width = ref(0)
  const height = ref(0)
  const minWidth = ref('')
  const minHeight = ref('')
  const disableSpacing = ref(false)

  onMounted(() => {
    slotName.value = register(type, index)

    queueAdEvent((moltenBundle) => {
      moltenBundle.registerAdPositionLoadedHandler((position: any) => {
        const name = position.getName()
        const containsAd = position.containsAd()
        if (name === slotName.value && containsAd) {
          // The ad has been successfully loaded and rendered
          status.value = 'successful'
          const { adHeight, adWidth, adFormat } = position.ad

          width.value = Number(adWidth)
          height.value = Number(adHeight)

          if (minWidth.value === '') {
            minWidth.value = width.value + 'px'
          }

          if (minHeight.value === '') {
            minHeight.value = height.value + 'px'
          }

          // This type of ad does not get displayed inline but instead as a sticky ad at the bottom of the screen.
          // In this case, there must not be a min-height and also no spacing (padding/margin) around the ad slot
          // In the future we might identify more of those ad formats and update this line accordingly
          disableSpacing.value = adFormat === 'mobile-floorad'

          if (disableSpacing.value) {
            minHeight.value = '0px'
          }
        }
      })
    })

    queueAdEvent((moltenBundle) => {
      moltenBundle.registerAllAdsLoadedHandler(() => {
        const notSuccessful = status.value !== 'successful'
        if (notSuccessful) {
          // The ad failed to load
          status.value = 'failed'
        }
      })
    })

    queueAdEvent((moltenBundle) => {
      if (slotName.value) {
        // Push the slot name to the molten bundle to start loading the ad
        moltenBundle.push(slotName.value)
        status.value = 'loading'
      }
    })
  })

  return {
    slotName,
    status,
    width,
    height,
    minWidth,
    minHeight,
    disableSpacing
  }
}
